import { CircularProgress, IconButton, Tooltip } from '@ethings/ui-toolkit';
import { useSnackbar } from 'notistack';
import type { FC, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch } from '../../pages/list/use-search';
import { filterToQuery, includeDeletedFilters, trackEntityExport } from '../../util';
import { extractEntityFromUrl, getContentCategory } from '../../util/analytics-helpers';
import { BottomNavigationAction } from '../bottom-navigation';
import { Icon } from '../icons';
import { MicroPortalLauncher, MicroPortalLauncherProps } from '../micro-portal';
import { MiddleSpinner } from '../middle-spinner';
import { DataExportOverrides, DataExportSettings } from './data-export.utils';

interface DataExportLauncherProps {
  dataExportSettings: DataExportSettings;
  dataExportOverrides?: DataExportOverrides;
  isMobile: boolean;
}

export const DataExportLauncher: FC<DataExportLauncherProps> = ({ dataExportSettings, dataExportOverrides, isMobile }) => {
  const { t, i18n } = useTranslation(['common']);
  const { filters, text, showDeleted } = useSearch();
  const { enqueueSnackbar } = useSnackbar();

  const renderDesktopButton = (isLoading: boolean): JSX.Element => (
    <Tooltip arrow title={t('common:component.data-exporter.heading')}>
      <IconButton color="primary" data-testid="data-export-button" disabled={isLoading} >
        {isLoading ? (
          <MiddleSpinner size="1.25rem" />
        ) : (
          <Icon icon="DownloadIcon" fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );

  const renderMobileButton = (isLoading: boolean): JSX.Element => (
    <BottomNavigationAction
      data-testid="data-export-button"
      disabled={isLoading}
      icon={isLoading ? (
        <CircularProgress color="inherit" size="1.5rem" />
      ) : (
        <Icon icon="DownloadIcon" />
      )}
      label={t('common:component.data-exporter.heading')}
    />
  );

  const handleExportSuccess = (): void => {
    const pathname = window.location.pathname;
    const portalMatch = pathname.match(/\/(admin|track|manage)(?:[/#]|$)/);
    const portal = portalMatch ? portalMatch[1] : null;
    const entityName = extractEntityFromUrl(`/api/v1/${dataExportSettings.entityType}`);
    const contentCategory = portal && entityName ? getContentCategory(portal, dataExportSettings.entityType) : null;

    if (entityName && contentCategory) {
      trackEntityExport(entityName, contentCategory);
    }
  };

  const commonProps: MicroPortalLauncherProps = {
    app: 'data-exporter',
    data: {
      entityType: dataExportSettings.entityType,
      limit: dataExportSettings.limit.toString(),
      filters: dataExportOverrides?.getFilterString ?
        dataExportOverrides.getFilterString(filters)
        : JSON.stringify({ ...filterToQuery(filters), ...includeDeletedFilters(showDeleted) }),
      search: text,
      language: i18n.language,
      ...(dataExportOverrides?.getSort && { sort: dataExportOverrides.getSort() }),
    },
    onError: () => {
      enqueueSnackbar(t('common:component.data-exporter.hint.error'), { variant: 'error' });
    },
    onSuccess: handleExportSuccess,
    type: 'dialog',
  };

  return (
    <MicroPortalLauncher
      {...commonProps}
      element={isMobile ? renderMobileButton(false) : renderDesktopButton(false)}
      loadingElement={isMobile ? renderMobileButton(true) : renderDesktopButton(true)}
      width={{ xs: '80vw', sm: '600px' }}
      height={{ xs: '40vh', sm: '300px' }}
      isDismissible
    />
  );
};
